import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

import { MEDIAPIPE_CDN, MEDIAPIPE_MODELS } from '../constants';
import type { Detector, FrameResult, Rect, FaceLandmark } from '../types';

export class FaceDetector implements Detector {
  private landmarker: FaceLandmarker | null = null;

  private initialized = false;

  private initPromise: Promise<void> | null = null;

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      const vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_CDN);
      this.landmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: MEDIAPIPE_MODELS.faceLandmarker,
        },
        runningMode: 'VIDEO',
        numFaces: 1,
      });
      this.initialized = true;
    })();

    return this.initPromise;
  }

  async detect(video: HTMLVideoElement): Promise<Partial<FrameResult>> {
    if (!this.initialized || !this.landmarker) {
      await this.initialize();
    }

    if (!this.landmarker) {
      return {};
    }

    try {
      const results = this.landmarker.detectForVideo(video, performance.now());

      if (!results.faceLandmarks || results.faceLandmarks.length === 0) {
        return {
          face: {
            detected: false,
            boundingBox: { x: 0, y: 0, width: 0, height: 0 },
            landmarks: [],
            confidence: 0,
          },
        };
      }

      const landmarks = results.faceLandmarks[0];
      const boundingBox = FaceDetector.computeBoundingBox(landmarks);

      return {
        face: {
          detected: true,
          boundingBox,
          landmarks: landmarks.map(l => ({
            x: l.x,
            y: l.y,
            z: l.z,
          })),
          confidence: 0.95,
        },
      };
    } catch {
      return {
        face: {
          detected: false,
          boundingBox: { x: 0, y: 0, width: 0, height: 0 },
          landmarks: [],
          confidence: 0,
        },
      };
    }
  }

  private static computeBoundingBox(landmarks: FaceLandmark[]): Rect {
    if (landmarks.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    const xs = landmarks.map(l => l.x);
    const ys = landmarks.map(l => l.y);

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  destroy(): void {
    if (this.landmarker) {
      this.landmarker.close();
      this.landmarker = null;
      this.initialized = false;
    }
  }
}
